import { Alert, Anchor, InlineFeedback, Paragraph, Text } from '@zillow/constellation';
import type { AlertProps, SpacingMixinProps } from '@zillow/constellation';
import { CicBackgroundReportStatus, CreditReportStatus } from '@zg-rentals/ts-capps-client';
import type { BackgroundReport, CreditReportType } from '@zg-rentals/ts-capps-client';
import type { RetrieveReportsResponse } from '../../../models/reports';
import {
  APPLICATIONS_FAQ_URL,
  RENTAL_APPLICATION_REPORTS_LAWS_URL,
  SUPPORT_REQUEST_URL,
} from '../../../constants/urls';
import { first } from 'lodash-es';

type BackgroundReportAlertSource = 'backgroundReport' | 'criminalReport' | 'evictionReport' | 'creditReport';

type BackgroundReportAlertBase = {
  content: React.ReactNode;
  summary: React.ReactNode;
  sources: Partial<Record<BackgroundReportAlertSource, boolean>>;
};

type BackgroundReportAlert = BackgroundReportAlertBase & {
  sources:
    | {
        backgroundReport: true;
      }
    | {
        creditReport: true;
      };
};

export function getReportAlerts(
  reportData: Partial<RetrieveReportsResponse> | undefined,
): Array<BackgroundReportAlert> {
  if (!reportData) return [];
  const { creditReport, backgroundReport } = reportData;
  return [...getCreditReportAlerts(creditReport), ...getBackgroundReportAlerts(backgroundReport)];
}

export function getCreditReportAlerts(creditReport: CreditReportType | undefined): Array<BackgroundReportAlert> {
  const result: Array<BackgroundReportAlert> = [];
  if (!creditReport) return result;
  if (
    ![
      CreditReportStatus.AVAILABLE,
      CreditReportStatus.DECEASED,
      CreditReportStatus.TOO_MANY_RECORDS,
      CreditReportStatus.LACK_OF_INFORMATION,
    ].includes(creditReport.reportStatus! as CreditReportStatus)
  ) {
    result.push({
      content: (
        <Text>
          A report can’t be pulled due to an error. Please contact{' '}
          <Anchor href={APPLICATIONS_FAQ_URL} target="_blank">
            customer support
          </Anchor>{' '}
          to report the issue. You can submit your application without the reports.
        </Text>
      ),
      summary: 'Report unavailable',
      sources: {
        creditReport: true,
      },
    });
  }
  return result;
}

export function getBackgroundReportAlerts(
  backgroundReport: BackgroundReport | undefined,
): Array<BackgroundReportAlert> {
  const result: Array<BackgroundReportAlert> = [];
  if (backgroundReport == null) return result;

  const evictionReportStatus = backgroundReport.evictionReport?.reportStatus;
  const criminalReportStatus = backgroundReport.criminalReport?.reportStatus;

  if (
    evictionReportStatus === CicBackgroundReportStatus.MANUAL_REVIEW ||
    criminalReportStatus === CicBackgroundReportStatus.MANUAL_REVIEW
  ) {
    // When both are in MANUAL REVIEW:
    const both =
      criminalReportStatus === CicBackgroundReportStatus.MANUAL_REVIEW &&
      evictionReportStatus === CicBackgroundReportStatus.MANUAL_REVIEW;

    const alertText: Pick<BackgroundReportAlert, 'content' | 'summary'> = both
      ? {
          content:
            'Background report is under manual review due to regulatory requirements. The report should automatically refresh in 1 to 2 business days.',
          summary: 'Report unavailable',
        }
      : {
          content: `${criminalReportStatus === CicBackgroundReportStatus.MANUAL_REVIEW ? 'Criminal' : 'Housing court'} record is under manual review due to regulatory requirements. The report should automatically refresh in 1 to 2 business days.`,
          summary: 'Report under manual review',
        };

    result.push({
      ...alertText,
      sources: {
        backgroundReport: true,
        evictionReport: evictionReportStatus === CicBackgroundReportStatus.MANUAL_REVIEW,
        criminalReport: criminalReportStatus === CicBackgroundReportStatus.MANUAL_REVIEW,
      },
    });
  }

  if (evictionReportStatus === CicBackgroundReportStatus.REGULATORY_RESTRICTIONS) {
    result.push({
      summary: (
        <Text>
          Some records are unavailable due to{' '}
          <Anchor href={RENTAL_APPLICATION_REPORTS_LAWS_URL} target="_blank">
            local laws
          </Anchor>
        </Text>
      ),
      content: null,
      sources: {
        backgroundReport: true,
        evictionReport: true,
      },
    });
  }

  if (criminalReportStatus === CicBackgroundReportStatus.REGULATORY_RESTRICTIONS) {
    result.push({
      summary: (
        <Text>
          Some records are unavailable due to{' '}
          <Anchor href={RENTAL_APPLICATION_REPORTS_LAWS_URL} target="_blank">
            local laws
          </Anchor>
        </Text>
      ),
      content: null,
      sources: {
        backgroundReport: true,
        criminalReport: true,
      },
    });
  }

  if (
    evictionReportStatus === CicBackgroundReportStatus.SECURITY_FREEZE ||
    criminalReportStatus === CicBackgroundReportStatus.SECURITY_FREEZE
  ) {
    result.push({
      content: <BackgroundReportSecurityFreezeContent />,
      summary: 'Report unavailable',
      sources: {
        backgroundReport: true,
        criminalReport: criminalReportStatus === CicBackgroundReportStatus.SECURITY_FREEZE,
        evictionReport: evictionReportStatus === CicBackgroundReportStatus.SECURITY_FREEZE,
      },
    });
  }

  if (backgroundReport.reportStatus === CicBackgroundReportStatus.NO_SSN_DOB) {
    result.push({
      content: <BackgroundReportReAuthContent />,
      summary: 'Report unavailable',
      sources: {
        backgroundReport: true,
      },
    });
  }

  if (
    evictionReportStatus === CicBackgroundReportStatus.IN_DISPUTE &&
    criminalReportStatus === CicBackgroundReportStatus.IN_DISPUTE
  ) {
    result.push({
      content:
        'Background check can’t be pulled at this time. The report should automatically refresh with the complete information within 1 business day.',
      summary: 'Background check unavailable',
      sources: {
        backgroundReport: true,
        criminalReport: true,
        evictionReport: true,
      },
    });
  }

  if (evictionReportStatus === CicBackgroundReportStatus.IN_DISPUTE) {
    result.push({
      content:
        'Housing court record can’t be pulled at this time. The record should automatically refresh with the complete information within 1 business day.',
      summary: 'Housing court record unavailable',
      sources: {
        backgroundReport: true,
        evictionReport: true,
      },
    });
  }

  if (criminalReportStatus === CicBackgroundReportStatus.IN_DISPUTE) {
    result.push({
      content:
        'Criminal report can’t be pulled at this time. The report should automatically refresh with the complete information within 1 business day.',
      summary: 'Criminal report unavailable',
      sources: {
        backgroundReport: true,
        criminalReport: true,
      },
    });
  }

  if (
    [CicBackgroundReportStatus.INVALID_SSN, CicBackgroundReportStatus.INVALID_DOB].includes(
      criminalReportStatus as CicBackgroundReportStatus,
    )
  ) {
    result.push({
      content: (
        <Text>
          Some of the report information can’t be pulled due to an error. Please contact{' '}
          <Anchor href={APPLICATIONS_FAQ_URL} target="_blank">
            customer support
          </Anchor>{' '}
          to report the issue. You can submit your application without the full report.
        </Text>
      ),
      summary: 'Criminal report unavailable',
      sources: {
        backgroundReport: true,
        criminalReport: true,
      },
    });
  }

  if (
    criminalReportStatus === CicBackgroundReportStatus.SERVICE_FAILED ||
    evictionReportStatus === CicBackgroundReportStatus.SERVICE_FAILED
  ) {
    result.push({
      content: (
        <Text>
          A report can’t be pulled due to an error. Please contact{' '}
          <Anchor href={APPLICATIONS_FAQ_URL} target="_blank">
            customer support
          </Anchor>{' '}
          to report the issue. You can submit your application without the reports.
        </Text>
      ),

      summary: 'Report unavailable',
      sources: {
        backgroundReport: true,
        criminalReport: true,
        evictionReport: true,
      },
    });
  }

  return result;
}

/**
 * Returns the description of the report status if the status implies an
 * unavailable report. Returns null for AVAILABLE status.
 */
export function getReportStatusDescription(
  reportStatus: CreditReportStatus | CicBackgroundReportStatus | null | undefined,
) {
  // TODO (RA-7938) - Handle credit report statuses, or create separate functions to handle credit and backgrond reports
  if (reportStatus === CicBackgroundReportStatus.AVAILABLE) {
    return null;
  }
  if (
    reportStatus == null ||
    [
      (CicBackgroundReportStatus.SERVICE_FAILED,
      CicBackgroundReportStatus.IN_DISPUTE,
      CicBackgroundReportStatus.MANUAL_REVIEW),
    ].includes(reportStatus as unknown as CicBackgroundReportStatus)
  ) {
    return 'Pending';
  }
  if (
    [CicBackgroundReportStatus.INVALID_SSN, CicBackgroundReportStatus.INVALID_DOB].includes(
      reportStatus as unknown as CicBackgroundReportStatus,
    )
  ) {
    return 'Error';
  }

  // Unidentified bug non-available status
  return 'Unavailable';
}

/** Returns true if one of these conditions is met:
 * - the report status is not AVAILABLE
 * - the report status is null
 */
export function isReportUnavailable(reportStatus: CreditReportStatus | CicBackgroundReportStatus | null | undefined) {
  if (reportStatus === CicBackgroundReportStatus.REGULATORY_RESTRICTIONS) {
    // The report with REGULATORY_RESTRICTIONS status
    // is viewable but the modal shows an alert summary,
    // so it is not considered unavailable
    return false;
  }
  return getReportStatusDescription(reportStatus) != null;
}

/**True if the user should be able to open the background check modal*/
export function isBackgroundCheckViewable(backgroundReport: BackgroundReport | undefined) {
  if (!backgroundReport) return false;

  return !(
    (isReportUnavailable(backgroundReport.criminalReport?.reportStatus) &&
      isReportUnavailable(backgroundReport.evictionReport?.reportStatus)) ||
    isReportUnavailable(backgroundReport.reportStatus)
  );
}

export function isCreditReportViewable(creditReport: CreditReportType | undefined) {
  if (!creditReport) return false;
  return !isReportUnavailable(creditReport?.reportStatus as CreditReportStatus);
}

function isSourceIncludedInAlert(
  alertSources: Partial<Record<BackgroundReportAlertSource, boolean>>,
  onlySources: Array<BackgroundReportAlertSource>,
) {
  for (const source of onlySources) {
    if (!alertSources[source]) {
      // The alert source is only included in the "onlySources" array
      return false;
    }
  }
  return true;
}

/** Gets the first (the order is order of items in the generator functions)
 * report alert that has at least one alert source included in the "onlySources" array
 */
export function getFirstReportAlert(
  alerts: Array<BackgroundReportAlert>,
  onlySources: Array<BackgroundReportAlertSource>,
) {
  return first(alerts.filter((x) => isSourceIncludedInAlert(x.sources, onlySources)));
}

interface CriminalReportAlertProps {
  reportData?: Partial<RetrieveReportsResponse>;
  onlySources?: Array<BackgroundReportAlertSource>;
}

/**
 * Conditionally shows an alert depending on the criminal report status
 */
export function ReportAlert({
  reportData,
  onlySources = [],
  ...alertProps
}: CriminalReportAlertProps & AlertProps & SpacingMixinProps) {
  const alert = getFirstReportAlert(getReportAlerts(reportData), onlySources)?.content;

  if (!alert) return null;
  return <Alert appearance="warning" body={alert} {...alertProps} />;
}

interface BackgroundReportAlertProps {
  backgroundReport?: BackgroundReport;
  onlySources?: Array<BackgroundReportAlertSource>;
}

export function BackgroundReportAlert({
  backgroundReport,
  onlySources = [],
  ...alertProps
}: BackgroundReportAlertProps & AlertProps & SpacingMixinProps) {
  const alert = getFirstReportAlert(getBackgroundReportAlerts(backgroundReport), onlySources)?.content;

  if (!alert) return null;

  return <Alert appearance="warning" body={alert} {...alertProps} />;
}

interface BackgroundReportAlertSmallProps {
  backgroundReport?: BackgroundReport;
  onlySources?: Array<BackgroundReportAlertSource>;
}

export function BackgroundReportAlertSmall({ backgroundReport, onlySources = [] }: BackgroundReportAlertSmallProps) {
  const alert = getFirstReportAlert(getBackgroundReportAlerts(backgroundReport), onlySources)?.summary;

  if (!alert) return null;

  return <InlineFeedback appearance="warning">{alert}</InlineFeedback>;
}

interface CreditReportAlertSmallProps {
  creditReport?: CreditReportType;
  onlySources?: Array<BackgroundReportAlertSource>;
}

export function CreditReportAlertSmall({ creditReport, onlySources = [] }: CreditReportAlertSmallProps) {
  const alert = getFirstReportAlert(getCreditReportAlerts(creditReport), onlySources)?.summary;

  if (!alert) return null;

  return <InlineFeedback appearance="warning">{alert}</InlineFeedback>;
}

export function BackgroundReportReAuthContent() {
  return (
    <Text>
      Your background check can’t be pulled with the information you provided during identity verification. Try
      re-entering the information, or contact{' '}
      <Anchor href={SUPPORT_REQUEST_URL} target="_blank">
        customer support
      </Anchor>
      .
    </Text>
  );
}

export function BackgroundReportSecurityFreezeContent() {
  return (
    <>
      <Paragraph>
        You have frozen your background check. You can still submit your application, but we recommend that you
        communicate the status of this report to the landlord, to ensure that your application is fully considered.
      </Paragraph>
      <br />
      <Paragraph>
        To unfreeze your background check, contact CIC customer assistance. Once unfrozen, the report will automatically
        become viewable within one business day.
      </Paragraph>
    </>
  );
}
